import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import GenericPlanner from "./components/GenericPlanner/GenericPlanner";
import { StudentType } from "../../../types/studentTypes";
import { Spin, Alert, Card } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { getStudent } from "../../../services/studentService";
import useGenericPlanner from "./hooks/useGenericPlanner";
import StudentSelectorWrapper from "../../../components/Peoples/guardian/StudentSelectorWrapper";
import { useEffectiveStudentId } from "../../../components/Peoples/guardian/hooks/useEffectiveStudentId";
import PageHeader from "components/Common/PageHeader";
import CustomAlert from "components/Common/ui/CustomAlert";
import { all_routes } from "router/all_routes";
import Loader from "components/Common/loader";

// Custom hook to fetch student data
const useStudentData = (studentId: string | undefined) => {
  const [data, setData] = useState<StudentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentId) {
      setLoading(false);
      return;
    }

    const fetchStudent = async () => {
      try {
        setError(null);
        setLoading(true);
        const response = await getStudent(studentId);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching student:', err);
        setError('Failed to load student data');
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [studentId]);

  return { student: data, loading, error };
};



/**
 * Student Routine Page Component
 * 
 * Displays a student's class schedule using the GenericPlanner component.
 * Gets the current student ID from Redux and fetches relevant data.
 */
const StudentRoutinePage = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.authSlice.user);
  // Get effective student ID (handles both student and guardian roles)
  const studentId = useEffectiveStudentId();

  // Fetch student data using custom hook
  const {
    student,
    loading: studentLoading,
    error: studentError
  } = useStudentData(studentId);

  // Extract class ID from student data
  const classId = useMemo(() => student?.class?.id, [student]);

  // Memoize the parameters for useGenericPlanner to prevent infinite re-renders
  const plannerParams = useMemo(() => ({
    role: "student" as const,
    classIds: classId ? [classId] : []
  }), [classId]);

  // Fetch planner data using the existing useGenericPlanner hook
  const {
    data: plannerData,
    isLoading: plannerLoading,
    error: plannerError
  } = useGenericPlanner(plannerParams);

  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "my_routine", active: true },
  ];

  return (

    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("my_routine")}
          breadcrumbItems={breadcrumbItems}
        />

        <div >
          <div >
            <StudentSelectorWrapper>
              {studentId ? (
                <>
                  {studentLoading || plannerLoading ? (
                    <div className="d-flex justify-content-center my-5">
                      <Loader/>
                    </div>
                  ) : studentError || plannerError ? (
                    <CustomAlert message={studentError || plannerError || ""} type="error" showIcon className="mb-3" />
                  ) : !student ? (
                    <CustomAlert message={t("alert.student_not_found.message")} description={t("alert.student_not_found.description")} type="warning" showIcon />
                  ) : !classId ? (
                    <CustomAlert message={t("alert.no_class_assigned_for_student.message")} description={t("alert.no_class_assigned_for_student.description")} type="warning" showIcon />
                  ) : (
                    <div className="">
                      <GenericPlanner role="student" data={plannerData} />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-danger">{t("select_student_first")}</p>
              )}
            </StudentSelectorWrapper>
          </div>
        </div>
      </div>
    </div>


  );
};




export default StudentRoutinePage;
